import {
  AdjustmentsHorizontalIcon,
  CardDesignControls,
  CardDesignText,
  CheckIcon,
  createPreviewStyle,
  CursorArrowRaysIcon,
  placeholder,
  useEffect,
  useRef,
  useState,
  XMarkIcon,
} from "@";

import { getCardDesignInitValues } from "../utils/helpers";

const ELEMENTS = [
  { id: "setGiverName", title: "اسم المُهدي", disclaimer: 0 },
  { id: "setRecipientName", title: "اسم المهدى إليه", disclaimer: 1 },
  { id: "setProjectName", title: "اسم المشروع", disclaimer: 2 },
  { id: "setNumberOfShares", title: "عدد الأسهم", disclaimer: 3 },
];

const ACTIVE_OUTLINE_COLOR = "#7e00da";

export default function GifCardDesign({ title, close, save, data }) {
  const imgRef = useRef(null);
  const hasCalcImageWidth = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [activeId, setActiveId] = useState("setGiverName");

  const giverInitialValues = getCardDesignInitValues(data, 30, 30, 0);
  const recipientInitialValues = getCardDesignInitValues(data, 30, 30, 1);
  const projectInitialValues = getCardDesignInitValues(data, 30, 30, 2);
  const numberInitialValues = getCardDesignInitValues(data, 30, 30, 3);

  const [giverName, setGiverName] = useState(giverInitialValues);
  const [projectName, setProjectName] = useState(projectInitialValues);
  const [numberOfShares, setNumberOfShares] = useState(numberInitialValues);
  const [recipientName, setRecipientName] = useState(recipientInitialValues);

  const stateMap = {
    setGiverName: { state: giverName, setter: setGiverName },
    setRecipientName: { state: recipientName, setter: setRecipientName },
    setProjectName: { state: projectName, setter: setProjectName },
    setNumberOfShares: { state: numberOfShares, setter: setNumberOfShares },
  };

  const handleChange = (setter) => (property, value) => {
    if (property === "x") {
      const elementIdMap = {
        setGiverName,
        setRecipientName,
        setProjectName,
        setNumberOfShares,
      };

      const elementId = Object.keys(elementIdMap).find(
        (key) => elementIdMap[key] === setter,
      );

      if (elementId) {
        const element = document.getElementById(elementId);
        const elementWidth = element ? element.offsetWidth : 0;

        const elementRightPx = (value * imageSize.width) / 100;
        let elementLeftPx = elementRightPx + elementWidth;
        elementLeftPx = Math.max(0, elementLeftPx);
        const maxLeftPx = imageSize.width - elementWidth;
        elementLeftPx = Math.min(elementLeftPx, maxLeftPx);
        const newXPercentage = (elementLeftPx / imageSize.width) * 100;

        setter((prev) => ({
          ...prev,
          xWithTextWidth: Math.ceil(newXPercentage),
        }));
      }
    }
    setter((prev) => ({ ...prev, [property]: value }));
  };

  const updaters = {
    setGiverName: handleChange(setGiverName),
    setRecipientName: handleChange(setRecipientName),
    setProjectName: handleChange(setProjectName),
    setNumberOfShares: handleChange(setNumberOfShares),
  };

  const handleSave = () => {
    save([giverName, recipientName, projectName, numberOfShares]);
  };

  useEffect(() => {
    updaters.setGiverName("disclaimer", 0);
    updaters.setRecipientName("disclaimer", 1);
    updaters.setProjectName("disclaimer", 2);
    updaters.setNumberOfShares("disclaimer", 3);

    const imgElement = imgRef.current;

    const handleImageLoad = () => {
      const imageWidth = imgElement.naturalWidth;
      const imageHeight = imgElement.naturalHeight;

      setImageSize({
        width: imageWidth,
        height: imageHeight,
      });
    };

    if (imgElement) {
      if (imgElement.complete) {
        handleImageLoad();
      } else {
        imgElement.addEventListener("load", handleImageLoad);
      }
    }

    return () => {
      if (imgElement) {
        imgElement.removeEventListener("load", handleImageLoad);
      }
    };
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (imageSize.width > 0) {
        const elements = [
          { id: "setGiverName", setter: setGiverName, state: giverName },
          {
            id: "setRecipientName",
            setter: setRecipientName,
            state: recipientName,
          },
          { id: "setProjectName", setter: setProjectName, state: projectName },
          {
            id: "setNumberOfShares",
            setter: setNumberOfShares,
            state: numberOfShares,
          },
        ];

        elements.forEach(({ id, setter, state }) => {
          const element = document.getElementById(id);
          const elementWidth = element ? element.offsetWidth : 0;

          const elementRightPx = (state.x * imageSize.width) / 100;
          let elementLeftPx = elementRightPx - elementWidth;
          elementLeftPx = Math.max(0, elementLeftPx);
          const maxLeftPx = imageSize.width - elementWidth;
          elementLeftPx = Math.min(elementLeftPx, maxLeftPx);
          const newXPercentage = (elementLeftPx / imageSize.width) * 100;

          setter((prev) => ({
            ...prev,
            x: Math.ceil(newXPercentage),
          }));
        });
      }

      hasCalcImageWidth.current = true;
    });

    const elementIds = [
      "setGiverName",
      "setRecipientName",
      "setProjectName",
      "setNumberOfShares",
    ];
    elementIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) resizeObserver.observe(element);
    });

    if (!hasCalcImageWidth.current) {
      return () => resizeObserver.disconnect();
    }
    setIsLoading(false);
  }, [imageSize]);

  const buildPreviewStyle = (id, state) => {
    const base = createPreviewStyle(state);
    if (activeId === id && state.show) {
      return {
        ...base,
        outline: `2px dashed ${ACTIVE_OUTLINE_COLOR}`,
        outlineOffset: 4,
        transition: "outline-color .2s",
      };
    }
    return base;
  };

  return (
    <div className="fixed inset-0 z-[99] flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="z-20 flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white/80 px-4 py-3 backdrop-blur-md dark:border-gray-700 dark:bg-gray-800/80">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-300">
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold text-gray-800 dark:text-gray-100">
              {title}
            </h1>
          </div>
        </div>

        <div className="hidden items-center gap-2 sm:flex">
          <button
            type="button"
            onClick={close}
            className="inline-flex items-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            <XMarkIcon className="h-4 w-4" />
            إلغاء
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-purple-900 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-purple-700/20 transition hover:bg-purple-800 dark:bg-purple-900 dark:hover:bg-purple-600"
          >
            <CheckIcon className="h-4 w-4" />
            حفظ التعديلات
          </button>
        </div>
      </header>

      {/* Main */}
      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_420px]">
        {/* Preview area */}
        <section className="relative flex items-start justify-center overflow-auto p-4 lg:p-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(148,163,184,0.35) 1px, transparent 1px)",
              backgroundSize: "18px 18px",
            }}
          />

          <div className="relative z-10 max-w-full">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {imageSize.width > 0 && (
                <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 px-3 py-1 text-[11px] font-medium text-gray-500 backdrop-blur dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-purple-700" />
                  {imageSize.width} × {imageSize.height} بكسل
                </div>
              )}
              <div className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50/80 px-3 py-1 text-[11px] font-medium text-purple-700 backdrop-blur dark:border-purple-900/60 dark:bg-purple-950/40 dark:text-purple-300">
                <CursorArrowRaysIcon className="h-3.5 w-3.5" />
                اسحب أي نص على البطاقة لتغيير موضعه
              </div>
            </div>

            {isLoading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm dark:bg-gray-900/70">
                <svg
                  aria-hidden="true"
                  className="h-8 w-8 animate-spin fill-purple-700 text-gray-200 dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            )}

            <div className="relative inline-block overflow-hidden bg-white">
              <img
                alt="gift card design preview"
                loading="lazy"
                className="block max-w-full"
                style={{ width: "auto", height: "auto" }}
                src={data?.imageView || data?.imageFullUrl || placeholder}
                ref={imgRef}
              />

              <CardDesignText
                id="setGiverName"
                title="اسم المهدي"
                style={buildPreviewStyle("setGiverName", giverName)}
                onActivate={() => setActiveId("setGiverName")}
                onDrag={(x, y) => {
                  updaters.setGiverName("x", x);
                  updaters.setGiverName("y", y);
                }}
              />

              <CardDesignText
                id="setProjectName"
                title="اسم المشروع"
                style={buildPreviewStyle("setProjectName", projectName)}
                onActivate={() => setActiveId("setProjectName")}
                onDrag={(x, y) => {
                  updaters.setProjectName("x", x);
                  updaters.setProjectName("y", y);
                }}
              />

              <CardDesignText
                id="setNumberOfShares"
                title="عدد الاسهم"
                style={buildPreviewStyle("setNumberOfShares", numberOfShares)}
                onActivate={() => setActiveId("setNumberOfShares")}
                onDrag={(x, y) => {
                  updaters.setNumberOfShares("x", x);
                  updaters.setNumberOfShares("y", y);
                }}
              />

              <CardDesignText
                id="setRecipientName"
                title="اسم المهدى إليه"
                style={buildPreviewStyle("setRecipientName", recipientName)}
                onActivate={() => setActiveId("setRecipientName")}
                onDrag={(x, y) => {
                  updaters.setRecipientName("x", x);
                  updaters.setRecipientName("y", y);
                }}
              />
            </div>
          </div>
        </section>

        {/* Controls panel */}
        <aside className="flex flex-col overflow-y-scroll border-t border-gray-200 bg-white/60 backdrop-blur dark:border-gray-700 dark:bg-gray-900/40 lg:border-s lg:border-t-0">
          {/* Element picker chips */}
          <div className="shrink-0 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              العناصر
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ELEMENTS.map((el) => {
                const { state } = stateMap[el.id];
                const isActive = activeId === el.id;
                return (
                  <button
                    key={el.id}
                    type="button"
                    onClick={() => setActiveId(el.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      isActive
                        ? "border-purple-900 bg-purple-900 text-white dark:border-purple-700 dark:bg-purple-800"
                        : "border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    } ${!state.show ? "opacity-60" : ""}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        state.show
                          ? isActive
                            ? "bg-white"
                            : "bg-purple-700"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    />
                    {el.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stacked panels */}
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {ELEMENTS.map((el) => {
              const { state } = stateMap[el.id];
              return (
                <CardDesignControls
                  key={el.id}
                  id={el.id}
                  title={el.title}
                  state={state}
                  update={updaters[el.id]}
                  isActive={activeId === el.id}
                  onActivate={() => setActiveId(el.id)}
                />
              );
            })}
          </div>
        </aside>
      </div>

      {/* Mobile sticky footer */}
      <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:hidden">
        <button
          type="button"
          onClick={close}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
        >
          <XMarkIcon className="h-4 w-4" />
          إلغاء
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-purple-900 px-4 py-2.5 text-sm font-semibold text-white"
        >
          <CheckIcon className="h-4 w-4" />
          حفظ
        </button>
      </footer>
    </div>
  );
}
